// =============================================================================
// supplier-announcements-client.ts — Client API de la RÉCEPTION FOURNISSEUR d'annonces JeKo (R15-COM-07,
// DBU §3.8.3 canal descendant / §3.9 onglet « Annonces JeKo reçues »).
// =============================================================================
// Miroir HTTP des endpoints FOURNISSEUR (session opaque, withTenant(supplier_siret)) :
//   GET   /supplier/announcements[?campaignId=…]  → liste (tri date d'envoi desc, autorité API)
//   GET   /supplier/announcements/:announcementId → détail + content_rich (LECTURE SEULE)
//   PATCH /supplier/announcements/:announcementId/read → accusé de lecture (read_at) [MUTATION]
//
// LECTURE SEULE (canal unidirectionnel descendant) : aucune règle métier ici (autorité = API : siret +
// contactId de la session, tri, statut lu/non-lu). AUCUNE donnée métier en localStorage (RULE_REF_006).
// Horodatages = ISO 8601 (string). Le `contentRich` est du HTML riche DÉJÀ sanitizé serveur ; le rendu
// le re-nettoie en défense en profondeur (RichHtmlView) — jamais de `dangerouslySetInnerHTML` non gardé.

/** Une ligne de l'onglet « Annonces JeKo reçues » (DBU §3.9 : titre · campagne · date d'envoi · statut). */
export interface SupplierAnnouncementListItemDto {
  readonly announcementId: string;
  readonly deliveryId: string;
  readonly titre: string | null;
  readonly campaignId: string;
  /** Intitulé de la campagne d'origine, ou `null` (campagne clôturée/illisible) — jamais inventé. */
  readonly campaignIntitule: string | null;
  /** Date d'envoi (ISO 8601) ou `null` — clé du tri par défaut (décroissant). */
  readonly sentAt: string | null;
  /** Accusé de lecture (ISO 8601) ou `null` = non lue. */
  readonly readAt: string | null;
}

/** Onglet « Annonces JeKo reçues » (liste triée date d'envoi décroissante, autorité API). */
export interface SupplierAnnouncementListDto {
  readonly items: readonly SupplierAnnouncementListItemDto[];
}

/** Détail d'une annonce reçue (LECTURE SEULE) — inclut le contenu enrichi (sanitizé serveur + re-rendu sûr). */
export interface SupplierAnnouncementDetailDto {
  readonly announcementId: string;
  readonly titre: string | null;
  readonly campaignId: string;
  readonly campaignIntitule: string | null;
  readonly sentAt: string | null;
  readonly readAt: string | null;
  readonly contentRich: string;
}

/** Accusé de lecture (idempotent) : l'instant de première lecture (ISO 8601) ou `null`. */
export interface SupplierAnnouncementReadAckDto {
  readonly announcementId: string;
  readonly readAt: string | null;
}

/** Genre d'erreur applicative, mappé pour un message UX explicite. */
export type SupplierAnnouncementsApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API annonces reçues (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle
 * ne réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class SupplierAnnouncementsApiError extends Error {
  public readonly kind: SupplierAnnouncementsApiErrorKind;

  public constructor(
    message: string,
    kind: SupplierAnnouncementsApiErrorKind = 'generic',
  ) {
    super(message);
    this.name = 'SupplierAnnouncementsApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client RÉCEPTION FOURNISSEUR — miroir des endpoints COM-07. LECTURE SEULE + accusé de lecture :
 * le siret/contactId viennent de la session (jamais du body) ; tri/statut sont l'autorité de l'API.
 */
export interface SupplierAnnouncementsClient {
  /** GET liste des annonces reçues (filtre optionnel par campagne d'origine). */
  list(campaignId?: string): Promise<SupplierAnnouncementListDto>;
  /** GET détail (LECTURE SEULE) d'une annonce reçue, avec son contenu enrichi. */
  getDetail(announcementId: string): Promise<SupplierAnnouncementDetailDto>;
  /** PATCH accusé de lecture (read_at) — MUTATION (CSRF double-submit). Idempotent. */
  markRead(announcementId: string): Promise<SupplierAnnouncementReadAckDto>;
}
